import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress, Snackbar, Alert } from '@mui/material';
import { useAuth } from './context/AuthContext';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import AppLayout from './components/layout/AppLayout';
import DashboardPage from './pages/DashboardPage';
import ProjectsPage from './pages/ProjectsPage';
import SprintsPage from './pages/SprintsPage';
import TasksPage from './pages/TasksPage';
import AssigneesPage from './pages/AssigneesPage';
import DailyGoalsPage from './pages/DailyGoalsPage';
import CallTopicsPage from './pages/CallTopicsPage';
import RefinementsPage from './pages/RefinementsPage';
import ScopePage from './pages/ScopePage';
import ReportPage from './pages/ReportPage';
import CsvImportPage from './pages/CsvImportPage';
import { useAppDispatch, useAppSelector } from './redux/hooks';
import { clearToast } from './redux/uiSlice';

function Protected({ children }: { children: JSX.Element }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

export default function App() {
  const toast = useAppSelector((s) => s.ui.toast);
  const dispatch = useAppDispatch();

  return (
    <>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot" element={<ForgotPasswordPage />} />

        <Route
          path="/"
          element={
            <Protected>
              <AppLayout />
            </Protected>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="projects" element={<ProjectsPage />} />
          <Route path="sprints" element={<SprintsPage />} />
          <Route path="tasks" element={<TasksPage />} />
          <Route path="assignees" element={<AssigneesPage />} />
          <Route path="daily-goals" element={<DailyGoalsPage />} />
          <Route path="call-topics" element={<CallTopicsPage />} />
          <Route path="refinements" element={<RefinementsPage />} />
          <Route path="scope" element={<ScopePage />} />
          <Route path="report" element={<ReportPage />} />
          <Route path="import" element={<CsvImportPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => dispatch(clearToast())}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert
            severity={toast.severity}
            onClose={() => dispatch(clearToast())}
            variant="filled"
          >
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
}
